/* =========================================================
   CIRCLE D SPECIALTIES — Phase 2B PDP guided variant picker
   Progressive enhancement (vanilla JS, defer-safe).

   WHAT IT DOES
   - On the top-10 SKUs only, wraps the EXISTING Empire variant
     option selects (.options-selection__input-select, driven by
     the <variant-selection> custom element) + any Globo Product
     Options fields (.gpo-element) into numbered, collapsible
     "step cards".
   - Never removes/replaces the underlying inputs: each control is
     re-parented INTO a wrapper that stays inside its original
     container, so change events still bubble to Empire / Globo and
     variant-id + price + line-item properties keep working.
   - Adds a progress indicator, a live build-summary panel, and
     gates the Add-to-cart button until every step is "done".

   SAFETY
   - Bails out cleanly (leaving the stock form untouched) if the
     product isn't in CDS_PDP_HANDLES, if it isn't a product page,
     or if the expected control markup can't be found.
   ========================================================= */
(function () {
  'use strict';

  /* ---- Config: gate to these handles (easy to extend) ---- */
  var CDS_PDP_HANDLES = [
    '6l80e-pro-series-258mm',
    '4l80e-pro-series-258mm',
    '350400-pro-series-252mm',
    'ford-pro-series-10r80-torque-converter',
    '4l80e-pro-series-245mm',
    'cds-ls-billet-flexplate',
    'bwm-10-pro-series-8hp-torque-converter',
    '4l60-pro-series-245mm',
    '8l90-pro-series-258mm',
    'gm-264mm-pro-series-pg-350-400-torque-converter'
  ];

  // Store-wide rollout: enhance EVERY product page that has >= STEP_MIN steps
  // and the expected markup. CDS_PDP_HANDLES stays as an optional override /
  // allowlist; when CDS_PDP_ALL is false it reverts to allowlist-only gating.
  var CDS_PDP_ALL = true;

  var FLAG = 'data-cds-pdp';        // guard so we only enhance once
  var STEP_MIN = 2;                 // < this many steps => skip stepper (few-option products)

  /* ---------------- guards ---------------- */
  function isProductPage() {
    return !!(document.body && document.body.classList.contains('template-product'))
      || !!document.querySelector('variant-selection, form[action="/cart/add"]');
  }

  function currentHandle() {
    // /products/<handle> possibly followed by / or query
    var m = (location.pathname || '').match(/\/products\/([^\/?#]+)/);
    return m ? m[1] : '';
  }

  function inScope() {
    // Store-wide: any product page is a candidate. The real gate is markup +
    // step-count, enforced later in build(); the allowlist is only consulted
    // when store-wide rollout is disabled.
    if (CDS_PDP_ALL) return isProductPage();
    return CDS_PDP_HANDLES.indexOf(currentHandle()) !== -1;
  }

  /* --------------- utilities --------------- */
  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  function text(node) { return node ? (node.textContent || '').replace(/\s+/g, ' ').trim() : ''; }

  // Read the live, single-variant price the theme keeps in sync.
  function livePriceText() {
    // Empire renders the active price inside .price.product__price; when a
    // single variant is resolved the .price__current shows it. Prefer the
    // first non-empty .money that is NOT a compare-at strike price.
    var scope = document.querySelector('.price.product__price') || document.querySelector('[class*="product"][class*="price"]');
    if (!scope) return '';
    var current = scope.querySelector('.price__current .money, .price__regular .money');
    if (current && text(current)) return text(current);
    var monies = scope.querySelectorAll('.money');
    for (var i = 0; i < monies.length; i++) {
      var m = monies[i];
      if (m.className.indexOf('compare-at') !== -1) continue;
      var t = text(m);
      if (t && t !== '$0' && t !== '$0.00') return t;
    }
    return '';
  }

  /* --------- discover the option / globo steps ---------- */
  // A "step" = { kind, label, block, isDone(), onChange(cb) }
  function collectSteps() {
    var steps = [];

    // 1) Native variant option selects (Empire).
    var optSelects = document.querySelectorAll('.options-selection__input-select');
    Array.prototype.forEach.call(optSelects, function (sel) {
      var block = sel.closest('.options-selection__select') || sel.parentElement;
      var lab = block.querySelector('.options-selection__select-label');
      steps.push({
        kind: 'variant',
        required: true,
        label: text(lab) || sel.getAttribute('data-variant-option-name') || 'Option',
        block: block,
        control: sel,
        valueText: function () {
          var o = sel.options[sel.selectedIndex];
          return o ? text(o) : '';
        }
      });
    });

    // 2) Globo Product Options fields (line-item properties).
    var gpoEls = document.querySelectorAll('.gpo-element');
    Array.prototype.forEach.call(gpoEls, function (gel) {
      var lab = gel.querySelector('.gpo-label');
      var required = gel.className.indexOf('required') !== -1
        || !!(lab && lab.className.indexOf('required') !== -1);
      steps.push({
        kind: 'globo',
        required: required,
        label: text(lab) || 'Options',
        block: gel,
        control: null,
        valueText: function () { return globoValueText(gel); }
      });
    });

    return steps;
  }

  // Best-effort readable value for a Globo field of any input type.
  function globoValueText(gel) {
    var parts = [];
    var checks = gel.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked');
    Array.prototype.forEach.call(checks, function (c) {
      var l = gel.querySelector('label[for="' + c.id + '"]') || c.closest('label') || (c.nextElementSibling);
      var t = l ? text(l) : (c.value || '');
      if (t) parts.push(t.replace(/\(\+.*?\)/, '').trim());
    });
    var sels = gel.querySelectorAll('select');
    Array.prototype.forEach.call(sels, function (s) {
      var o = s.options[s.selectedIndex];
      if (o && o.value) parts.push(text(o));
    });
    var texts = gel.querySelectorAll('input[type="text"], input[type="number"], textarea');
    Array.prototype.forEach.call(texts, function (t) {
      if (t.value && t.value.trim()) parts.push(t.value.trim());
    });
    return parts.join(', ');
  }

  // A Globo step is "done" if it has a value, OR if it's optional (optional
  // fields never block ATC — they just show "None" until chosen).
  function stepIsDone(step, touched) {
    if (step.kind === 'globo') {
      if (!step.required) return true;              // optional never blocks
      return !!step.valueText();
    }
    // Native variant selects always have a default value; require an explicit
    // touch so the customer actively confirms each spec.
    return !!touched[step._i];
  }

  /* ------------------- build UI ------------------- */
  function build() {
    if (!isProductPage() || !inScope()) return false;

    var form = document.querySelector('form[action="/cart/add"], form[action^="/cart/add"]');
    if (!form || form.getAttribute(FLAG)) return false;

    var steps = collectSteps();

    // Few-option / no-option products: skip the stepper, leave stock form.
    if (steps.length < STEP_MIN) return false;

    // Locate the ATC button (Empire).
    var atc = form.querySelector('.product-form--atc-button')
      || form.querySelector('button[name="add"]')
      || form.querySelector('button[type="submit"]');
    if (!atc) return false;

    form.setAttribute(FLAG, '1');
    document.body.classList.add('cds-pdp-on');

    steps.forEach(function (s, i) { s._i = i; });
    var touched = {};

    /* --- Progress + summary shell, inserted before the first step block --- */
    var firstBlock = steps[0].block;
    var host = el('div', 'cds-pdp');
    var progress = el('div', 'cds-pdp__progress',
      '<div class="cds-pdp__progress-track"><span class="cds-pdp__progress-fill"></span></div>' +
      '<div class="cds-pdp__progress-label">Step 1 of ' + steps.length + '</div>');
    host.appendChild(progress);

    // Insert the progress host just before the first option block.
    firstBlock.parentNode.insertBefore(host, firstBlock);

    /* --- Wrap each step block into a collapsible card, IN PLACE ---
       Critical: we build the card next to the block within the block's OWN
       parent, then move the block into the card body. This keeps every
       control inside its original ancestor (Empire's <variant-selection> for
       native selects, Globo's .gpo-container for .gpo-element), so both apps'
       delegated/direct change listeners survive. We do NOT clone nodes. */
    var cards = steps.map(function (step, i) {
      var card = el('div', 'cds-pdp-card');
      card.setAttribute('data-step', String(i));

      var head = el('button', 'cds-pdp-card__head');
      head.type = 'button';
      head.innerHTML =
        '<span class="cds-pdp-card__num">' + (i + 1) + '</span>' +
        '<span class="cds-pdp-card__title">' + escapeHtml(step.label) + '</span>' +
        '<span class="cds-pdp-card__value"></span>' +
        '<span class="cds-pdp-card__chevron" aria-hidden="true"></span>' +
        '<span class="cds-pdp-card__lock" aria-hidden="true"></span>';

      var body = el('div', 'cds-pdp-card__body');

      // Place the card exactly where the block currently sits, then re-parent
      // the block into the card body (still a descendant of the same ancestor).
      var parent = step.block.parentNode;
      parent.insertBefore(card, step.block);
      body.appendChild(step.block);

      card.appendChild(head);
      card.appendChild(body);

      head.addEventListener('click', function () {
        if (card.classList.contains('is-locked')) return;
        setActive(i);
      });

      return { card: card, head: head, body: body, step: step };
    });

    /* --- Build summary panel, placed right before the ATC --- */
    var summary = el('div', 'cds-pdp-summary',
      '<div class="cds-pdp-summary__title">Your build</div>' +
      '<ul class="cds-pdp-summary__list"></ul>' +
      '<div class="cds-pdp-summary__price-row"><span>Price</span><strong class="cds-pdp-summary__price"></strong></div>');
    var atcHost = atc.closest('.product-form--atc') || atc.parentNode;
    atcHost.parentNode.insertBefore(summary, atcHost);

    // Gate the ATC.
    atc.classList.add('cds-pdp-atc');
    var origAtcHtml = atc.innerHTML;

    var active = 0;

    function setActive(i) {
      active = i;
      cards.forEach(function (c, idx) {
        c.card.classList.toggle('is-open', idx === i);
      });
      refresh();
    }

    function firstIncompleteRequired() {
      for (var i = 0; i < steps.length; i++) {
        if (steps[i].required && !stepIsDone(steps[i], touched)) return i;
      }
      return steps.length; // all done
    }

    function escapeHtmlLocal(s) { return escapeHtml(s); }

    function refresh() {
      var doneCount = 0;
      var maxUnlocked = 0;

      // A step is "unlocked" once all prior REQUIRED steps are done.
      var allPriorDone = true;
      cards.forEach(function (c, idx) {
        var step = c.step;
        var done = stepIsDone(step, touched);
        if (done) doneCount++;

        var unlocked = allPriorDone || idx === active;
        c.card.classList.toggle('is-locked', !unlocked && idx !== active);
        c.card.classList.toggle('is-done', done && idx !== active);

        // value summary in the collapsed head
        var vt = step.valueText();
        var vEl = c.head.querySelector('.cds-pdp-card__value');
        if (done && vt) {
          vEl.textContent = vt;
          vEl.classList.add('has-value');
        } else if (!step.required && !vt) {
          vEl.textContent = 'Optional';
          vEl.classList.remove('has-value');
        } else {
          vEl.textContent = '';
          vEl.classList.remove('has-value');
        }

        if (unlocked) maxUnlocked = idx;
        if (step.required && !done) allPriorDone = false;
      });

      // progress
      var requiredSteps = steps.filter(function (s) { return s.required; });
      var requiredDone = requiredSteps.filter(function (s) { return stepIsDone(s, touched); }).length;
      var totalReq = requiredSteps.length || steps.length;
      var pct = Math.round((requiredDone / totalReq) * 100);
      var fill = progress.querySelector('.cds-pdp__progress-fill');
      if (fill) fill.style.width = pct + '%';
      var stepNum = Math.min(firstIncompleteRequired() + 1, steps.length);
      progress.querySelector('.cds-pdp__progress-label').textContent =
        (requiredDone >= totalReq)
          ? 'Build complete'
          : 'Step ' + stepNum + ' of ' + steps.length;

      // summary list
      var list = summary.querySelector('.cds-pdp-summary__list');
      list.innerHTML = steps.map(function (s) {
        var vt = s.valueText();
        var done = stepIsDone(s, touched);
        var val = (done && vt) ? vt : (s.required ? '<em>Not selected</em>' : 'None');
        return '<li><span class="cds-pdp-summary__k">' + escapeHtmlLocal(s.label) + '</span>' +
          '<span class="cds-pdp-summary__v">' + (done && vt ? escapeHtmlLocal(val) : val) + '</span></li>';
      }).join('');
      summary.querySelector('.cds-pdp-summary__price').textContent = livePriceText() || '—';

      // ATC gate
      var allReqDone = requiredDone >= totalReq;
      atc.classList.toggle('cds-pdp-atc--ready', allReqDone);
      if (allReqDone) {
        atc.removeAttribute('data-cds-disabled');
        atc.innerHTML = origAtcHtml;
      } else {
        atc.setAttribute('data-cds-disabled', '1');
        atc.innerHTML = '<span class="cds-pdp-atc__hint">Complete your build</span>';
      }
    }

    // Block ATC clicks while the build is incomplete (capture phase so we win
    // before Empire's submit handler). We do NOT disable the native button
    // attribute — that could interfere with Empire's own state management.
    atc.addEventListener('click', function (e) {
      if (atc.getAttribute('data-cds-disabled')) {
        e.preventDefault();
        e.stopPropagation();
        // nudge focus to first incomplete step
        var idx = firstIncompleteRequired();
        if (idx < steps.length) setActive(idx);
      }
    }, true);

    // Wire change/interaction on each step's controls.
    cards.forEach(function (c, idx) {
      var step = c.step;
      var handler = function () {
        touched[idx] = true;
        refresh();
        // auto-advance to next not-done step for guided flow
        var next = idx + 1;
        while (next < steps.length && stepIsDone(steps[next], touched)) next++;
        if (next < steps.length) {
          setActive(next);
        } else {
          setActive(idx); // stay; build likely complete
        }
      };
      // native selects
      if (step.control) {
        step.control.addEventListener('change', handler);
        // Re-picking the pre-selected value fires no `change` (the 6-bolt
        // flexplate trap), so also count an opened-then-dismissed picker as
        // confirmation: the select blurs with a value while still untouched.
        // `armed` keeps programmatic focus/blur (Empire init) from confirming.
        var armed = false;
        step.control.addEventListener('pointerdown', function () { armed = true; });
        step.control.addEventListener('keydown', function () { armed = true; });
        step.control.addEventListener('blur', function () {
          if (armed && !touched[idx] && step.valueText()) handler();
        });
        // Deterministic way past a pre-selected step: picking the same value
        // emits NO DOM event on any platform (macOS native menus especially),
        // so blur-confirm only helps once focus happens to leave the select.
        // An explicit confirm button always works, on every device.
        var confirmBtn = el('button', 'cds-pdp-card__confirm');
        confirmBtn.type = 'button';
        confirmBtn.textContent = (idx === steps.length - 1) ? 'Confirm selection' : 'Confirm — next step';
        confirmBtn.addEventListener('click', handler);
        c.body.appendChild(confirmBtn);
      }
      // globo: listen broadly for any input change inside the block
      if (step.kind === 'globo') {
        step.block.addEventListener('change', handler);
        step.block.addEventListener('input', function () { touched[idx] = true; refresh(); });
      }
    });

    // Keep summary price in sync with Empire's own async variant updates.
    var priceScope = document.querySelector('.price.product__price');
    if (priceScope && window.MutationObserver) {
      var mo = new MutationObserver(function () {
        summary.querySelector('.cds-pdp-summary__price').textContent = livePriceText() || '—';
      });
      mo.observe(priceScope, { childList: true, subtree: true, characterData: true });
    }

    setActive(0);
    refresh();
    return true;
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* =======================================================================
     SPEC TABLE + FEATURES (Task 2)
     Empire renders the product description in .product-description.rte.
     Converter PDPs carry two kinds of prose:
       - "Key - Value" / "Key: Value" spec lines (e.g.
         "Converter Diameter - 258mm - 10 1/4\"") — these belong in a
         two-column SPECIFICATIONS table.
       - plain feature bullets (e.g. "Furnace Brazed") — these become a
         red-check FEATURES checklist.
     Spec K-V lines live BOTH inside <li> bullets AND inside the leading
     <p>...<br>...</p> intro blocks, so we harvest candidate lines from both.
     GATING: per spec, if the description has no <ul>, do nothing. The
     original description node is HIDDEN (not removed) so nothing is lost and
     any theme/app hooks on it survive.
     ======================================================================= */
  var SPEC_FLAG = 'data-cds-spec';

  // Decide whether a line is a "Key - Value" / "Key: Value" spec.
  // Returns [key, value] or null. Conservative: the key must be short-ish and
  // the value must be non-trivial so feature bullets don't leak into specs.
  function parseSpecLine(raw) {
    var s = (raw || '').replace(/\s+/g, ' ').trim();
    if (!s) return null;
    // Prefer a colon split, else a spaced-hyphen split (" - "). Never split on
    // hyphens that are part of ranges/words (handled by requiring spaces).
    var m = s.match(/^([^:]{1,40}):\s*(.+)$/);
    if (!m) {
      m = s.match(/^(.{1,40}?)\s[-–]\s(.+)$/); // " - " or " – "
    }
    if (!m) return null;
    var key = m[1].trim().replace(/[.:–-]+$/, '').trim();
    var val = m[2].trim();
    if (!key || !val) return null;
    // Guard against false positives: key should read like a label (letters),
    // value should carry a unit/measurement — that's what makes it a spec
    // rather than a feature sentence or a marketing callout.
    if (!/[a-z]/i.test(key)) return null;
    // Reject overly long "keys" that are really full sentences.
    if (key.split(' ').length > 6) return null;
    // Reject keys that END in a number: those are almost always a range
    // sentence fragment, e.g. "Available in stall speeds from 2800" - "3700 rpm".
    if (/\d\s*$/.test(m[1].trim())) return null;
    // The value must carry a real UNIT token (dimension / measurement), not
    // merely any digit — this filters instruction sentences like
    // "C6 & C7 Corvette Owners - Please select application...".
    var hasUnit = /(\d)\s*("|”|″|'|in\b|inch|mm\b|cm\b|rpm\b|lb\b|lbs\b|hp\b|nm\b|°|deg\b)/i.test(val)
      || /\bratio\b/i.test(val)
      || /^\s*\d[\d\s./x×-]*("|”|″|mm|in|rpm)?\s*$/i.test(val); // pure dimension e.g. 258mm - 10 1/4"
    if (!hasUnit) return null;
    // Reject values that read as a full instruction/marketing sentence
    // (many words, no leading measurement).
    if (val.split(' ').length > 8 && !/^\d/.test(val)) return null;
    return [key, val];
  }

  function buildSpecTable() {
    if (!isProductPage()) return false;
    var desc = document.querySelector('.product-description.rte')
      || document.querySelector('[data-product-description]')
      || document.querySelector('.product-block--description .rte');
    if (!desc || desc.getAttribute(SPEC_FLAG)) return false;

    // Per spec: no <ul> anywhere in the description => do nothing.
    var uls = desc.querySelectorAll('ul');
    if (!uls.length) return false;

    // --- Harvest candidate lines ---
    var specs = [];     // [ [key,val], ... ]
    var features = [];  // [ "text", ... ]
    var seen = {};

    function pushLine(txt) {
      var t = (txt || '').replace(/\s+/g, ' ').trim();
      if (!t) return;
      var kv = parseSpecLine(t);
      if (kv) {
        var k = kv[0].toLowerCase();
        if (seen['s:' + k]) return;
        seen['s:' + k] = 1;
        specs.push(kv);
      } else {
        var key = 'f:' + t.toLowerCase();
        if (seen[key]) return;
        seen[key] = 1;
        features.push(t);
      }
    }

    // 1) <li> bullets => spec or feature.
    Array.prototype.forEach.call(uls, function (ul) {
      Array.prototype.forEach.call(ul.querySelectorAll('li'), function (li) {
        pushLine(text(li));
      });
    });

    // 2) Leading <p> intro blocks: split on <br> and check each fragment for a
    //    K-V spec. Feature-free prose (marketing sentences) is left untouched.
    Array.prototype.forEach.call(desc.querySelectorAll('p'), function (p) {
      // Split the paragraph HTML on <br> so multi-line intro specs separate.
      var frags = (p.innerHTML || '').split(/<br\s*\/?>/i);
      if (frags.length < 2) {
        // single-line <p>: only harvest if it's clearly a spec, never a feature
        var single = text(p);
        if (parseSpecLine(single)) pushLine(single);
        return;
      }
      frags.forEach(function (frag) {
        var tmp = document.createElement('div');
        tmp.innerHTML = frag;
        var t = text(tmp);
        if (parseSpecLine(t)) pushLine(t); // only pull specs from paragraphs
      });
    });

    // Nothing structured worth showing? Leave the stock description alone.
    if (!specs.length && !features.length) return false;

    // --- Build the injected block ---
    var host = el('div', 'cds-specs');

    if (specs.length) {
      var specHtml = '<div class="cds-specs__title">Specifications</div>' +
        '<dl class="cds-specs__table">' +
        specs.map(function (kv) {
          return '<div class="cds-specs__row">' +
            '<dt class="cds-specs__k">' + escapeHtml(kv[0]) + '</dt>' +
            '<dd class="cds-specs__v">' + escapeHtml(kv[1]) + '</dd></div>';
        }).join('') +
        '</dl>';
      host.appendChild(el('div', 'cds-specs__block', specHtml));
    }

    if (features.length) {
      var featHtml = '<div class="cds-specs__title">Features</div>' +
        '<ul class="cds-feat">' +
        features.map(function (f) {
          return '<li class="cds-feat__item"><span class="cds-feat__check" aria-hidden="true"></span>' +
            '<span class="cds-feat__text">' + escapeHtml(f) + '</span></li>';
        }).join('') +
        '</ul>';
      host.appendChild(el('div', 'cds-specs__block', featHtml));
    }

    // Insert AFTER the original description node, then hide the original (keep
    // it in the DOM so nothing that reads it breaks).
    desc.setAttribute(SPEC_FLAG, '1');
    if (desc.parentNode) {
      if (desc.nextSibling) desc.parentNode.insertBefore(host, desc.nextSibling);
      else desc.parentNode.appendChild(host);
    }
    desc.classList.add('cds-desc-hidden');
    document.body.classList.add('cds-specs-on');
    return true;
  }

  /* --------------- init (defensive) --------------- */
  function init() {
    try {
      if (!inScope()) return;
      // Empire + Globo render async; retry a few times until controls exist.
      // Readiness is gated on the cart form only: variant selects may be
      // absent (simple products) yet we still want to inject the spec table.
      var tries = 0;
      var built = false;
      var specced = false;
      (function attempt() {
        tries++;
        var form = document.querySelector('form[action="/cart/add"], form[action^="/cart/add"]');
        var hasSelects = !!document.querySelector('.options-selection__input-select');

        // Spec/features table is independent of the stepper — try it once the
        // description block is present.
        if (!specced && document.querySelector('.product-description, [data-product-description], .product-block--description .rte')) {
          try { buildSpecTable(); } catch (e) { /* never break the PDP */ }
          specced = true;
        }

        // Stepper: needs the form + variant selects (or Globo) present.
        if (!built && form && (hasSelects || document.querySelector('.gpo-element'))) {
          try { built = build(); } catch (e) { built = false; /* leave stock form untouched */ }
        }

        // Keep retrying until both attempts have had a fair shot or timeout.
        var settled = (built || (form && tries > 6)) && specced;
        if (!settled && tries < 30) setTimeout(attempt, 300); // up to ~9s for slow apps
      })();
    } catch (e) { /* no-op: never break the stock PDP */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
